import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCRM } from '../contexts/CRMContext';
import ContactModal from '../components/Modals/ContactModal';
import ContactDetailModal from '../components/Modals/ContactDetailModal';
import toast from 'react-hot-toast';

const { FiPlus, FiEdit2, FiTrash2, FiMail, FiPhone, FiMapPin, FiUsers, FiEye, FiTestTube } = FiIcons;

const Contacts = () => {
  const { contacts, addContact, updateContact, deleteContact } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  console.log('📱 [Contacts] Component rendering...');
  console.log('📱 [Contacts] Current contacts:', contacts.length, 'items');
  console.log('📱 [Contacts] Search term:', searchTerm);

  // Test connection function
  const testConnection = async () => {
    console.log('🧪 [Contacts] Testing NocodeBackend connection...');
    const { nocodeBackendService } = await import('../services/nocodeBackendService');
    const result = await nocodeBackendService.testConnection();
    console.log('🧪 [Contacts] Connection test result:', result);
    
    if (result.success) {
      toast.success('Connection test successful!');
    } else {
      toast.error(`Connection test failed: ${result.error}`);
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('📱 [Contacts] Filtered contacts:', filteredContacts.length, 'items');

  const handleAddContact = () => {
    console.log('➕ [Contacts] Opening add contact modal');
    setEditingContact(null);
    setIsModalOpen(true);
  };

  const handleEditContact = (contact) => {
    console.log('✏️ [Contacts] Opening edit contact modal for:', contact.id, contact.name);
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleViewContact = (contact) => {
    console.log('👁️ [Contacts] Opening contact detail modal for:', contact.id, contact.name);
    setViewingContact(contact);
    setIsDetailModalOpen(true);
  };

  const handleDeleteContact = async (id) => {
    console.log('🗑️ [Contacts] Attempting to delete contact:', id);
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await deleteContact(id);
        toast.success('Contact deleted successfully');
        console.log('✅ [Contacts] Contact deleted successfully');
      } catch (error) {
        console.error('❌ [Contacts] Failed to delete contact:', error);
        toast.error('Failed to delete contact');
      }
    } else {
      console.log('🚫 [Contacts] Delete cancelled by user');
    }
  };

  const handleSaveContact = async (contactData, profilePicture) => {
    console.log('💾 [Contacts] Saving contact...', { contactData, hasProfilePicture: !!profilePicture });
    
    try {
      if (editingContact) {
        console.log('✏️ [Contacts] Updating existing contact:', editingContact.id);
        await updateContact(editingContact.id, contactData, profilePicture);
        toast.success('Contact updated successfully');
        console.log('✅ [Contacts] Contact updated successfully');
      } else {
        console.log('➕ [Contacts] Creating new contact');
        await addContact(contactData, profilePicture);
        toast.success('Contact added successfully');
        console.log('✅ [Contacts] Contact added successfully');
      }
      setIsModalOpen(false);
      setEditingContact(null);
    } catch (error) {
      console.error('❌ [Contacts] Failed to save contact:', error);
      
      // Show more detailed error message
      let errorMessage = 'Failed to save contact';
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contacts</h1>
          <p className="text-gray-600">Manage your business contacts and relationships</p>
        </div>
        <div className="flex space-x-3">
          {/* Debug button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={testConnection}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600 transition-colors"
            title="Test NocodeBackend Connection"
          >
            <SafeIcon icon={FiTestTube} className="h-5 w-5" />
            <span>Test Connection</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddContact}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="h-5 w-5" />
            <span>Add Contact</span>
          </motion.button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContacts.map((contact) => (
                <motion.tr
                  key={contact.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {contact.profile_picture_thumbnail ? (
                        <img
                          src={contact.profile_picture_thumbnail}
                          alt={contact.name}
                          className="h-10 w-10 rounded-full object-cover mr-3"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-white">
                            {contact.name?.charAt(0) || 'N'}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                        <div className="text-sm text-gray-500">{contact.position}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contact.company}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiMail} className="h-4 w-4 mr-2" />
                        {contact.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiPhone} className="h-4 w-4 mr-2" />
                        {contact.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2" />
                      {contact.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleViewContact(contact)}
                        className="text-green-600 hover:text-green-900"
                        title="View Details"
                      >
                        <SafeIcon icon={FiEye} className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleEditContact(contact)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit Contact"
                      >
                        <SafeIcon icon={FiEdit2} className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDeleteContact(contact.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Contact"
                      >
                        <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredContacts.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiUsers} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No contacts found</p>
            <p className="text-gray-400 text-sm">Add your first contact to get started</p>
          </div>
        )}
      </div>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => {
          console.log('🚫 [Contacts] Closing contact modal');
          setIsModalOpen(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
      />

      <ContactDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          console.log('🚫 [Contacts] Closing detail modal');
          setIsDetailModalOpen(false);
          setViewingContact(null);
        }}
        contact={viewingContact}
      />
    </div>
  );
};

export default Contacts;